import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type BenOption = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

/**
 * ClassCode encodes BOTH policy type AND occupation class:
 *
 *  Standard  Class I   → '996'
 *  Standard  Class II  → '996B'
 *  Standard  Class III → '996C'
 *
 *  Excelsior Class I   → '995'
 *  Excelsior Class II  → '995B'
 *  Excelsior Class III → '995C'
 */
export type ClassCode = '996' | '996B' | '996C' | '995' | '995B' | '995C' | '998';
export type OccClass  = 'Class I' | 'Class II' | 'Class III';

export interface PaTierBenefit {
  name: string;
  death: number;
  ptd: number;
  ttd_weekly: number;
  medical: number;
  appliances: number;
  last_expense: number;
}

export interface PaExcelsiorBenefit {
  name: string;
  death: number;
  permanent_total_disablement: number;
  temporary_total_disablement_per_week: number;
  medical_expenses: number;
  last_expense: number;
  artificial_appliances: number;
  wheel_chair: number;
  optical: number;
  hearing_aids: number;
  dental: number;
  local_evacuation: number;
}

export interface PaTierPremiumRates {
  'Class I': number;
  'Class II': number;
  'Class III': number;
}

export interface EsbStandardBenefitsResult {
  tierData: Record<BenOption, PaTierBenefit>;
  premiumRates: Record<BenOption, PaTierPremiumRates>;
}

export interface EsbExcelsiorBenefitsResult {
  excelsiorData: Record<BenOption, PaExcelsiorBenefit>;
  excelsiorPremiumRates: Record<BenOption, PaTierPremiumRates>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const TIER_NAMES: Record<BenOption, string> = {
  A: 'White', B: 'Blue', C: 'Orange', D: 'Bronze', E: 'Silver', F: 'Gold',
};

const STANDARD_CLASS_CODES: Record<OccClass, ClassCode> = {
  'Class I':   '996',
  'Class II':  '996B',
  'Class III': '996C',
};

const EXCELSIOR_CLASS_CODES: Record<OccClass, ClassCode> = {
  'Class I':   '995',
  'Class II':  '995B',
  'Class III': '995C',
};


const BEN_OPTIONS: BenOption[] = ['A', 'B', 'C', 'D', 'E', 'F'];
const OCC_CLASSES: OccClass[]  = ['Class I', 'Class II', 'Class III'];

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class EsbPaService {

  // ── Chatbot proxy URL ──────────────────────────────────────────────────────
  //
  //   Angular (breakout)  →  POST /bot/pa-breakout  (chatbot)  →  ESB GenPAbenefits
  //
  // Chatbot /bot/pa-breakout contract (when action='get-benefits'):
  //   Body:     { action: 'get-benefits', benOption, classCode, warCoverage, externalRefNumber }
  //   Response: raw ESB GenPAbenefits response
  //
  // ESB credentials live entirely on the chatbot backend (ESBGateway.js).
  // Angular never sees or sends any ESB credentials.

  private get paProxyUrl(): string {
    return environment.baseUrl.replace('/motor', '/pa-breakout');
  }

  // ── Unique ref per request ─────────────────────────────────────────────────

  private generateRefNumber(): string {
    return crypto.randomUUID();
  }

  // ── Single call ────────────────────────────────────────────────────────────

  /**
   * Makes one POST to the chatbot /pa-benefits proxy, which calls ESB internally.
   * No token or credentials are sent from Angular — the chatbot owns ESB auth.
   */
  async fetchBenefits(
    benOption: BenOption,
    classCode: ClassCode,
    warCoverage: 'Y' | 'N' = 'N',
  ): Promise<any> {
    const externalRefNumber = this.generateRefNumber();

    const requestBody = {
      action: 'get-benefits',
      benOption,
      classCode,
      warCoverage,
      externalRefNumber,
    };

    console.group(`%c[EsbPa] 📤 BenOption=${benOption} ClassCode=${classCode} War=${warCoverage}`, 'color: #2980b9');
    console.log('URL  →', this.paProxyUrl);
    console.log('Body →', requestBody);

    let data: any;
    try {
      const response = await fetch(this.paProxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      console.log('HTTP status ←', response.status, response.statusText);

      if (!response.ok) {
        console.error(`Non-2xx response for BenOption=${benOption} ClassCode=${classCode}`);
        console.groupEnd();
        throw new Error(`HTTP ${response.status} for BenOption=${benOption} ClassCode=${classCode}`);
      }

      data = await response.json();
      console.log('%c✓ Response ←', 'color: #27ae60; font-weight: bold', data);
    } catch (err) {
      console.error('fetchBenefits threw:', err);
      console.groupEnd();
      throw err;
    }

    console.groupEnd();
    return data;
  }

  // ── Batch fetch helpers ────────────────────────────────────────────────────

  /**
   * Fetches all 6 tiers × 3 occupation classes in parallel using the supplied
   * class-code map.  Throws if any individual call fails.
   */
  private async fetchAllRaw(
    classCodes: Record<OccClass, ClassCode>,
    label: string,
  ): Promise<Record<BenOption, Record<OccClass, any>>> {
    const matrix = {} as Record<BenOption, Record<OccClass, any>>;
    for (const opt of BEN_OPTIONS) {
      matrix[opt] = { 'Class I': null, 'Class II': null, 'Class III': null };
    }

    const calls = BEN_OPTIONS.flatMap((opt) =>
      OCC_CLASSES.map(async (cls) => {
        const code = classCodes[cls];
        try {
          matrix[opt][cls] = await this.fetchBenefits(opt, code);
        } catch (err) {
          throw new Error(`[${label}] Tier ${opt} / ${cls} (${code}): ${(err as Error).message}`);
        }
      })
    );

    const settled = await Promise.allSettled(calls);
    const failures = settled
      .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      .map((r) => r.reason?.message ?? String(r.reason));

    if (failures.length > 0) {
      throw new Error(failures.join('\n'));
    }

    return matrix;
  }

  // ── Response mappers ───────────────────────────────────────────────────────
  //
  // Chatbot proxy response shape (after ESBGateway parses the raw ESB response):
  // {
  //   "error": false,
  //   "error_code": "00",
  //   "benefits": [
  //     { "BenefitCode": 1,  "BenefitDescription": "Death",                          "BenefitLimit": 250000, "TotalPremium": 2426, ... },
  //     { "BenefitCode": 2,  "BenefitDescription": "Permanent Total Disablement",     "BenefitLimit": 250000, "TotalPremium": 2426, ... },
  //     { "BenefitCode": 3,  "BenefitDescription": "Temporary Total Disablement...",  "BenefitLimit": 2500,   "TotalPremium": 2426, ... },
  //     { "BenefitCode": 4,  "BenefitDescription": "Medical Expenses",               "BenefitLimit": 25000,  "TotalPremium": 2426, ... },
  //     { "BenefitCode": 5,  "BenefitDescription": "Last Expense",                   "BenefitLimit": 25000,  "TotalPremium": 2426, ... },
  //     { "BenefitCode": 6,  "BenefitDescription": "Artificial Appliances",          "BenefitLimit": 15000,  "TotalPremium": 2426, ... },
  //     { "BenefitCode": 7,  "BenefitDescription": "Wheel Chair",                    "BenefitLimit": 15000,  "TotalPremium": 2426, ... },
  //     { "BenefitCode": 8,  "BenefitDescription": "Optical",                        "BenefitLimit": 2500,   "TotalPremium": 2426, ... },
  //     { "BenefitCode": 9,  "BenefitDescription": "Hearing Aids",                   "BenefitLimit": 2500,   "TotalPremium": 2426, ... },
  //     { "BenefitCode": 10, "BenefitDescription": "Dental",                         "BenefitLimit": 2500,   "TotalPremium": 2426, ... },
  //     { "BenefitCode": 11, "BenefitDescription": "Local Evacuation",               "BenefitLimit": 15000,  "TotalPremium": 2426, ... }
  //   ]
  // }
  // TotalPremium is the same on every row — it is the policy-level annual premium.

  /** Validates the chatbot proxy response and returns the benefits array. */
  private parseResponse(raw: any, context: string): any[] {
    if (!raw) throw new Error(`No response data for ${context}`);
    if (raw.error) {
      throw new Error(`ESB error for ${context}: code=${raw.error_code} msg=${raw.message}`);
    }
    const arr = raw.benefits;
    if (!Array.isArray(arr) || arr.length === 0) {
      throw new Error(`benefits is empty or not an array for ${context}: ${JSON.stringify(raw)}`);
    }
    return arr;
  }

  /** Finds the row with the given BenefitCode and returns its BenefitLimit, or 0 if absent. */
  private benefitLimit(arr: any[], code: number, label: string): number {
    const row = arr.find((r: any) => r.BenefitCode === code);
    if (!row) {
      console.warn(`[EsbPa] BenefitCode ${code} (${label}) not in ESB response — defaulting to 0`);
      return 0;
    }
    const n = Number(row.BenefitLimit);
    return isNaN(n) ? 0 : n;
  }

  /** TotalPremium is the same on every row — read it from the first row. */
  private totalPremium(arr: any[], context: string): number {
    const n = Number(arr[0].TotalPremium);
    if (isNaN(n)) throw new Error(`TotalPremium is not a number for ${context}: ${arr[0].TotalPremium}`);
    return n;
  }

  // ── Mappers (accept pre-parsed arr so parseResponse runs only once per call) ─

  private mapToBenefit(benOption: BenOption, arr: any[]): PaTierBenefit {
    return {
      name:         TIER_NAMES[benOption],
      death:        this.benefitLimit(arr, 1, 'Death'),
      ptd:          this.benefitLimit(arr, 2, 'Permanent Total Disablement'),
      ttd_weekly:   this.benefitLimit(arr, 3, 'Temporary Total Disablement'),
      medical:      this.benefitLimit(arr, 4, 'Medical Expenses'),
      last_expense: this.benefitLimit(arr, 5, 'Last Expense'),
      appliances:   this.benefitLimit(arr, 6, 'Artificial Appliances'),
    };
  }

  private mapToExcelsiorBenefit(benOption: BenOption, arr: any[]): PaExcelsiorBenefit {
    return {
      name:                                 TIER_NAMES[benOption],
      death:                                this.benefitLimit(arr, 1,  'Death'),
      permanent_total_disablement:          this.benefitLimit(arr, 2,  'Permanent Total Disablement'),
      temporary_total_disablement_per_week: this.benefitLimit(arr, 3,  'Temporary Total Disablement'),
      medical_expenses:                     this.benefitLimit(arr, 4,  'Medical Expenses'),
      last_expense:                         this.benefitLimit(arr, 5,  'Last Expense'),
      artificial_appliances:                this.benefitLimit(arr, 6,  'Artificial Appliances'),
      wheel_chair:                          this.benefitLimit(arr, 7,  'Wheel Chair'),
      optical:                              this.benefitLimit(arr, 8,  'Optical'),
      hearing_aids:                         this.benefitLimit(arr, 9,  'Hearing Aids'),
      dental:                               this.benefitLimit(arr, 10, 'Dental'),
      local_evacuation:                     this.benefitLimit(arr, 11, 'Local Evacuation'),
    };
  }

  private mapToPremium(raw: any, benOption: BenOption, cls: OccClass): number {
    const arr = this.parseResponse(raw, `BenOption=${benOption} ${cls}`);
    return this.totalPremium(arr, `BenOption=${benOption} ${cls}`);
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  // ── On-demand single-call methods (called whenever user changes selection) ─

  /**
   * Fetches Standard benefits + premium for exactly ONE tier option / occupation class.
   * Makes ONE ESB call.  Called whenever the user changes tier or occupation.
   */
  async fetchSingleStandard(
    option: BenOption,
    occClass: OccClass,
    warCoverage: 'Y' | 'N' = 'N',
  ): Promise<{ premium: number; rawBenefits: { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[] }> {
    const classCode = STANDARD_CLASS_CODES[occClass];
    console.group(
      `%c[EsbPa] 📤 Standard on-demand: Option=${option} | Class=${occClass} (${classCode}) | War=${warCoverage}`,
      'color: #2980b9; font-weight: bold',
    );
    try {
      const raw = await this.fetchBenefits(option, classCode, warCoverage);
      const arr = this.parseResponse(raw, `Standard Option=${option} ${occClass} war=${warCoverage}`);
      const result = {
        premium:     this.totalPremium(arr, `Standard Option=${option} ${occClass} war=${warCoverage}`),
        rawBenefits: arr.map((b: any) => ({
          BenefitDescription: b.BenefitDescription,
          BenefitLimit: b.BenefitLimit,
          AnnualPremium: b.AnnualPremium,
          TLevy: b.TLevy,
          Pcf: b.Pcf,
          StampDuty: b.StampDuty,
        })),
      };
      console.log('%c✅ Standard benefit ready', 'color: #27ae60; font-weight: bold', {
        option, occClass, warCoverage, premium: result.premium,
      });
      console.groupEnd();
      return result;
    } catch (err) {
      console.error('[EsbPa] fetchSingleStandard failed:', err);
      console.groupEnd();
      throw err;
    }
  }

  /**
   * Fetches Excelsior benefits + premium for exactly ONE tier option / occupation class.
   * Makes ONE ESB call.  Called when the user enables Excelsior or changes their
   * selection while Excelsior is already enabled.
   * Pass warCoverage='Y' to get the war-inclusive premium.
   */
  async fetchSingleExcelsior(
    option: BenOption,
    occClass: OccClass,
    warCoverage: 'Y' | 'N' = 'N',
  ): Promise<{ premium: number; rawBenefits: { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[] }> {
    const classCode = EXCELSIOR_CLASS_CODES[occClass];
    console.group(
      `%c[EsbPa] 📤 Excelsior on-demand: Option=${option} | Class=${occClass} (${classCode}) | War=${warCoverage}`,
      'color: #8e44ad; font-weight: bold',
    );
    try {
      const raw = await this.fetchBenefits(option, classCode, warCoverage);
      const arr = this.parseResponse(raw, `Excelsior Option=${option} ${occClass} war=${warCoverage}`);
      const result = {
        premium:     this.totalPremium(arr, `Excelsior Option=${option} ${occClass} war=${warCoverage}`),
        rawBenefits: arr.map((b: any) => ({
          BenefitDescription: b.BenefitDescription,
          BenefitLimit: b.BenefitLimit,
          AnnualPremium: b.AnnualPremium,
          TLevy: b.TLevy,
          Pcf: b.Pcf,
          StampDuty: b.StampDuty,
        })),
      };
      console.log('%c✅ Excelsior benefit ready', 'color: #27ae60; font-weight: bold', {
        option, occClass, warCoverage, premium: result.premium,
      });
      console.groupEnd();
      return result;
    } catch (err) {
      console.error('[EsbPa] fetchSingleExcelsior failed:', err);
      console.groupEnd();
      throw err;
    }
  }

  /**
   * Fetches Student benefits + premium for ONE tier option.
   * Always classCode='998', warCoverage='N' — no occupation class, no excelsior, no passive war.
   */
  async fetchSingleStudent(
    option: BenOption,
  ): Promise<{ premium: number; rawBenefits: { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[] }> {
    console.group(
      `%c[EsbPa] 📤 Student on-demand: Option=${option} (998)`,
      'color: #16a085; font-weight: bold',
    );
    try {
      const raw = await this.fetchBenefits(option, '998', 'N');
      const arr = this.parseResponse(raw, `Student Option=${option}`);
      const result = {
        premium:     this.totalPremium(arr, `Student Option=${option}`),
        rawBenefits: arr.map((b: any) => ({
          BenefitDescription: b.BenefitDescription,
          BenefitLimit: b.BenefitLimit,
          AnnualPremium: b.AnnualPremium,
          TLevy: b.TLevy,
          Pcf: b.Pcf,
          StampDuty: b.StampDuty,
        })),
      };
      console.log('%c✅ Student benefit ready', 'color: #27ae60; font-weight: bold', { option, premium: result.premium });
      console.groupEnd();
      return result;
    } catch (err) {
      console.error('[EsbPa] fetchSingleStudent failed:', err);
      console.groupEnd();
      throw err;
    }
  }

  // ── Batch-load methods (kept for potential future use) ─────────────────────

  /** Fetches Standard benefit amounts + premiums for all 6 tiers × 3 classes. */
  async loadStandardBenefits(): Promise<EsbStandardBenefitsResult> {
    const raw = await this.fetchAllRaw(STANDARD_CLASS_CODES, 'Standard');

    const tierData     = {} as Record<BenOption, PaTierBenefit>;
    const premiumRates = {} as Record<BenOption, PaTierPremiumRates>;

    for (const opt of BEN_OPTIONS) {
      const benefitRaw = raw[opt]['Class I'] ?? raw[opt]['Class II'] ?? raw[opt]['Class III'];
      const benefitArr = this.parseResponse(benefitRaw, `Standard BenOption=${opt}`);
      tierData[opt] = this.mapToBenefit(opt, benefitArr);

      premiumRates[opt] = {
        'Class I':   this.mapToPremium(raw[opt]['Class I'],   opt, 'Class I'),
        'Class II':  this.mapToPremium(raw[opt]['Class II'],  opt, 'Class II'),
        'Class III': this.mapToPremium(raw[opt]['Class III'], opt, 'Class III'),
      };
    }

    console.log('%c[EsbPa] ✅ Standard benefits loaded', 'color: #27ae60; font-weight: bold',
      { tierData, premiumRates });
    return { tierData, premiumRates };
  }

  /** Fetches Excelsior benefit amounts + premiums for all 6 tiers × 3 classes. */
  async loadExcelsiorBenefits(): Promise<EsbExcelsiorBenefitsResult> {
    const raw = await this.fetchAllRaw(EXCELSIOR_CLASS_CODES, 'Excelsior');

    const excelsiorData         = {} as Record<BenOption, PaExcelsiorBenefit>;
    const excelsiorPremiumRates = {} as Record<BenOption, PaTierPremiumRates>;

    for (const opt of BEN_OPTIONS) {
      const benefitRaw = raw[opt]['Class I'] ?? raw[opt]['Class II'] ?? raw[opt]['Class III'];
      const benefitArr = this.parseResponse(benefitRaw, `Excelsior BenOption=${opt}`);
      excelsiorData[opt] = this.mapToExcelsiorBenefit(opt, benefitArr);

      excelsiorPremiumRates[opt] = {
        'Class I':   this.mapToPremium(raw[opt]['Class I'],   opt, 'Class I'),
        'Class II':  this.mapToPremium(raw[opt]['Class II'],  opt, 'Class II'),
        'Class III': this.mapToPremium(raw[opt]['Class III'], opt, 'Class III'),
      };
    }

    console.log('%c[EsbPa] ✅ Excelsior benefits loaded', 'color: #27ae60; font-weight: bold',
      { excelsiorData, excelsiorPremiumRates });
    return { excelsiorData, excelsiorPremiumRates };
  }
}
